import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Collapse,
  IconButton,
  Skeleton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search,
  KeyboardArrowDown,
  KeyboardArrowUp,
  OpenInNew,
  Apps,
  AdminPanelSettings,
  PhoneIphone,
  Language,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import { useAuth } from '../contexts/AuthContext';

interface AuditChange {
  field: string;
  oldValue: any;
  newValue: any;
}

interface AuditLog {
  _id: string;
  action: string;
  module: string;
  targetId: string;
  targetModel: string;
  targetDescription?: string;
  changes: AuditChange[];
  performedBy: {
    role: string;
    id: string;
    email: string;
    name: string;
    studioId?: string;
    studioName?: string;
  };
  studioId?: string;
  source?: string;
  metadata?: Record<string, any>;
  createdAt: string;
}

type AuditSourceTab = 'all' | 'admin_panel' | 'partner_app' | 'studio_web';

const ROLE_COLORS: Record<string, string> = {
  super_admin: '#7c3aed',
  studio_owner: '#0284c7',
  brand_owner: '#be185d',
  manager: '#059669',
  instructor: '#d97706',
  // Members acting for themselves on the booking website — without an entry
  // here every website row rendered in the same grey as an unrecognised actor.
  user: '#0f766e',
};

const MODULE_LABELS: Record<string, string> = {
  packs: 'Packs',
  bookings: 'Bookings',
  sessions: 'Sessions',
  events: 'Events',
  users: 'Users',
  wellness: 'Wellness',
  studios: 'Studios',
  classes: 'Classes',
  'studio-users': 'Studio Users',
  instructors: 'Instructors',
  waitlist: 'Waitlist',
  'user-notes': 'Member Notes',
};

const ACTION_LABELS: Record<string, string> = {
  WAITLIST_JOIN: 'Joined Waitlist (Member)',
  WAITLIST_LEAVE: 'Left Waitlist (Member)',
  WAITLIST_ADD_BY_STUDIO: 'Added to Waitlist (Staff)',
  WAITLIST_REMOVE_BY_STUDIO: 'Removed from Waitlist (Staff)',
  WAITLIST_REORDER: 'Reordered Waitlist',
  WAITLIST_BOOKED_BY_STUDIO: 'Booked from Waitlist (Staff)',
  WAITLIST_AUTO_BOOKED: 'Auto-booked from Waitlist',
  WAITLIST_SPOT_OPEN_NOTIFIED: 'Waitlist Notified: Spot Open',
  WAITLIST_SKIPPED: 'Skipped on Waitlist',
  WAITLIST_CLOSED_ALREADY_BOOKED: 'Waitlist Closed (Already Booked)',
  CREATE_USER_NOTE: 'Add Member Note',
  UPDATE_USER_NOTE: 'Edit Member Note',
  DELETE_USER_NOTE: 'Delete Member Note',
  DELETE_PACK: 'Delete Pack',
  DELETE_STUDIO_PACK: 'Delete Studio Pack',
  CREATE_PACK: 'Create Pack',
  CREATE_STUDIO_PACK: 'Create Studio Pack',
  UPDATE_PACK: 'Update Pack',
  UPDATE_STUDIO_PACK: 'Update Studio Pack',
  TOGGLE_PACK_STATUS: 'Toggle Pack Status',
  TOGGLE_STUDIO_PACK_STATUS: 'Toggle Studio Pack Status',
  DELETE_BOOKING: 'Delete Booking',
  UPDATE_BOOKING_STATUS: 'Update Booking Status',
  CREATE_MANUAL_BOOKING: 'Create Manual Booking',
  CREATE_ADMIN_WALK_IN: 'Admin Walk-In',
  AUTO_WALK_IN_FROM_PACK_EDIT: 'Auto Walk-In (Pack Edit)',
  CREATE_MANUAL_WELLNESS_BOOKING: 'Manual Wellness Booking',
  DECLARE_HOLIDAY: 'Declare Holiday',
  DELETE_FREEZE_HISTORY_ENTRY: 'Delete Freeze History',
  ASSIGN_PACK: 'Assign Pack',
  EDIT_PURCHASED_PACK: 'Edit Purchased Pack',
  DELETE_PURCHASED_PACK: 'Delete Pack Subscription',
  CANCEL_BOOKING: 'Cancel Booking',
  SELF_CANCEL_BOOKING: 'Cancel Booking (Self)',
  CANCEL_MANUAL_BOOKING: 'Cancel Manual Booking',
  FREEZE_PACK: 'Freeze Pack',
  SCHEDULE_PACK_FREEZE: 'Schedule Pack Freeze',
  DELETE_USER: 'Delete User',
  SELF_DELETE_USER: 'Delete Account (Self)',
  SELF_LEAVE_STUDIO: 'Leave Studio (Self)',
  REMOVE_STUDIO_MEMBER: 'Remove Studio Member',
  CREATE_STUDIO_USER: 'Create Studio Member',
  UPDATE_STUDIO_USER: 'Update Studio Member',
  UPDATE_USER: 'Update User',
  MARK_MANUAL_BOOKING_PAID: 'Mark Manual Booking Paid',
  MARK_BOOKING_PAID: 'Mark Booking Paid',
  SESSION_CHECK_IN: 'Session Check-In',
  SELF_CHECK_IN: 'Check-In (Self, QR)',
  SELF_CHECK_IN_ALREADY_CHECKED_IN: 'Check-In Attempt (Self, Already Checked In)',
  PACK_SCAN_CHECK_IN: 'Check-In (Self, Pack Scan)',
  PACK_SCAN_BOOKING: 'Booking (Self, Pack Scan)',
  QR_WALK_IN_CHECK_IN: 'Walk-In (Self, QR)',
  REQUEST_BOOKING_CANCELLATION: 'Request Booking Cancellation',
  APPROVE_BOOKING_CANCELLATION: 'Approve Cancellation Request',
  REJECT_BOOKING_CANCELLATION: 'Reject Cancellation Request',
  RESCHEDULE_BOOKING: 'Reschedule Booking',
  ARCHIVE_EXPIRED_BOOKINGS: 'Archive Expired Bookings',
  CANCEL_PACK_FREEZE: 'Cancel Pack Freeze',
  UNFREEZE_PACK: 'Unfreeze Pack',
  BACKFILL_USER_PROFILE: 'Backfill User Profile',
  CREATE_USER: 'Create User',
  LINK_EXISTING_USER_TO_STUDIO: 'Link Existing User to Studio',
  CREATE_STUDIO_USER_ACCOUNT: 'Create Studio Staff Account',
  UPDATE_STUDIO_USER_ACCOUNT: 'Update Studio Staff Account',
  DELETE_STUDIO_USER_ACCOUNT: 'Delete Studio Staff Account',
  CHANGE_STUDIO_USER_PASSWORD: 'Change Studio Staff Password',
  CREATE_SESSION: 'Create Session',
  UPDATE_SESSION: 'Edit Session',
  DELETE_SESSION: 'Delete Session',
  COPY_SESSIONS: 'Copy Sessions',
  BULK_CREATE_SESSIONS: 'Bulk Create Sessions',
  BULK_UPDATE_SESSIONS: 'Bulk Edit Sessions',
  BULK_DELETE_SESSIONS: 'Bulk Delete Sessions',
  BULK_DELETE_SESSIONS_BY_DATE: 'Delete All Sessions on Date',
  CREATE_EVENT: 'Create Event',
  UPDATE_EVENT: 'Edit Event',
  DELETE_EVENT: 'Delete Event',
  CREATE_CLASS: 'Create Class',
  UPDATE_CLASS: 'Edit Class',
  DELETE_CLASS: 'Delete Class',
  CREATE_CLASS_TYPE: 'Create Class Type',
  UPDATE_CLASS_TYPE: 'Edit Class Type',
  DELETE_CLASS_TYPE: 'Delete Class Type',
  REASSIGN_INSTRUCTOR_WORK: 'Reassign Instructor Work',
};

// Matches Mongo/ISO datetimes, e.g. 2026-08-20T05:30:00.000Z
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;
// Calendar-only dates, e.g. "2026-08-24" (istDate fields)
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
// 24h clock strings, e.g. "11:30" (istTime / hhmm fields)
const TIME_ONLY_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function humanizeKey(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const META_GRID_COLS = 4;

const metaGridSx = {
  display: 'grid',
  gridTemplateColumns: `repeat(${META_GRID_COLS}, minmax(0, 1fr))`,
  columnGap: 1.25,
  rowGap: 0.75,
};

const metaLabelSx = {
  bgcolor: '#f1f5f9',
  px: 0.75,
  py: 0.35,
  borderRadius: '3px 3px 0 0',
};

const metaSectionLabelSx = {
  bgcolor: '#f1f5f9',
  px: 0.75,
  py: 0.4,
  borderRadius: 0.5,
  mb: 0.5,
};

const MetaFieldCell: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Box sx={metaLabelSx}>
      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.2 }}>
        {label}
      </Typography>
    </Box>
    <Box sx={{ px: 0.75, py: 0.35, fontSize: '0.8125rem', lineHeight: 1.35, minHeight: 22 }}>
      {children}
    </Box>
  </Box>
);

const MetaFieldGrid: React.FC<{ entries: [string, any][] }> = ({ entries }) => (
  <Box sx={metaGridSx}>
    {entries.map(([k, v]) => (
      <MetaFieldCell key={k} label={humanizeKey(k)}>
        <MetaValue value={v} parentKey={k} />
      </MetaFieldCell>
    ))}
  </Box>
);

// Keys whose value never explains *what an operation did* — only echoes
// something the caller already sent, or a raw id already represented as a name.
const LOW_VALUE_METADATA_KEYS = new Set([
  'explicitSessionIds',
  'deletedIds',
  'seriesId',
  'userId',
  'eventId',
]);

// "0 skipped" / "0 failed" tells a reader nothing they didn't already
// assume — only show these counters when something actually happened.
const ZERO_IS_NOISE_KEY_RE = /(skipped|failed|error|excluded)count$/i;

function isEmptyMetaValue(v: any): boolean {
  return (
    v === null ||
    v === undefined ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length === 0)
  );
}

/**
 * Drops metadata fields that don't tell an admin anything about what the
 * operation actually did: empty arrays/objects ("Skipped Sessions: None"),
 * zero-valued skip/fail counters, request-shape echoes like
 * explicitSessionIds, and a "matched" count that's just a duplicate of the
 * headline count when nothing was filtered out along the way.
 */
function filterUsefulMetadataEntries(metadata: Record<string, any> | undefined): [string, any][] {
  const entries = Object.entries(metadata ?? {}).filter(([k]) => k !== 'userId');
  const map = Object.fromEntries(entries);
  const primaryCountKey = ['deletedCount', 'updatedCount', 'createdCount'].find((k) => k in map);
  const hasSample = 'sample' in map || 'deletedSample' in map;
  const primaryCount = primaryCountKey ? Number(map[primaryCountKey]) : null;
  const skippedCount = typeof map.skippedCount === 'number' ? map.skippedCount : 0;

  return entries.filter(([k, v]) => {
    if (LOW_VALUE_METADATA_KEYS.has(k)) return false;
    if (isEmptyMetaValue(v)) return false;
    if (typeof v === 'number' && v === 0 && ZERO_IS_NOISE_KEY_RE.test(k)) return false;
    if (k === 'matched' && primaryCount != null && (v === primaryCount || v === primaryCount + skippedCount)) {
      return false;
    }
    // Per-row sample already carries the before→after; repeating the uniform
    // `changes` blob (time, slots, instructor) next to it is the same fact twice.
    if (hasSample && (k === 'changes' || k === 'times' || k === 'instructor')) return false;
    if (k === 'times' && map.time != null) return false;
    if ((k === 'class' || k === 'classId') && (map.className || hasSample)) return false;
    return true;
  });
}

/** Shallow objects whose values are scalars or primitive arrays — flatten into the main field grid. */
function isFlattenableMetaObject(v: any): boolean {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const entries = Object.entries(v);
  if (entries.length === 0) return false;
  return entries.every(([, val]) => {
    if (val === null || val === undefined) return true;
    if (typeof val !== 'object') return true;
    if (Array.isArray(val)) {
      return val.length === 0 || val.every((item) => typeof item !== 'object' || item === null);
    }
    return false;
  });
}

function expandMetadataForGrid(entries: [string, any][]): {
  gridEntries: [string, any][];
  wideEntries: [string, any][];
} {
  const gridEntries: [string, any][] = [];
  const wideEntries: [string, any][] = [];

  for (const [k, v] of entries) {
    if (Array.isArray(v) && v.length > 0) {
      wideEntries.push([k, v]);
    } else if (isFlattenableMetaObject(v)) {
      for (const [nestedKey, nestedVal] of Object.entries(v)) {
        if (!isEmptyMetaValue(nestedVal)) {
          gridEntries.push([nestedKey, nestedVal]);
        }
      }
    } else if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
      wideEntries.push([k, v]);
    } else {
      gridEntries.push([k, v]);
    }
  }

  return { gridEntries, wideEntries };
}

function formatISTDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatDateOnly(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-IN', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatTimeOnly(timeStr: string): string {
  const [h, m] = timeStr.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;

type EntityKind = 'instructor' | 'class' | 'user' | 'studio';

const NAME_FIELDS = ['name', 'className', 'studioName', 'title', 'eventName', 'email'] as const;

function inferEntityKind(parentKey?: string): EntityKind | null {
  if (!parentKey) return null;
  const k = parentKey.replace(/[_-]+/g, '').toLowerCase();
  if (k.includes('instructor')) return 'instructor';
  if (k.includes('classtype')) return null;
  if (k === 'class' || k === 'classid' || k.endsWith('class') || k.includes('classid')) return 'class';
  if (k === 'user' || k === 'userid' || (k.endsWith('user') && !k.includes('name') && !k.includes('email'))) {
    return 'user';
  }
  if (k === 'studio' || k === 'studioid' || k === 'studios' || k.endsWith('studio')) return 'studio';
  return null;
}

function extractNamedRef(value: any): { id?: string; name?: string } | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    if (OBJECT_ID_RE.test(value)) return { id: value };
    return { name: value };
  }
  if (typeof value !== 'object' || Array.isArray(value)) return null;
  const idRaw = value.id ?? value._id ?? value.$oid;
  const id = idRaw != null && String(idRaw) !== '[object Object]' ? String(idRaw) : undefined;
  let name: string | undefined;
  for (const field of NAME_FIELDS) {
    if (typeof value[field] === 'string' && value[field].trim()) {
      name = value[field];
      break;
    }
  }
  if (!id && !name) return null;
  const keys = Object.keys(value);
  const onlyRefKeys = keys.every((k) =>
    k === 'id' || k === '_id' || k === '$oid' || (NAME_FIELDS as readonly string[]).includes(k),
  );
  if (!onlyRefKeys && !name) return null;
  return { id: id && OBJECT_ID_RE.test(id) ? id : id, name };
}

function isFromToObject(value: any): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  return keys.length > 0 && keys.every((k) => k === 'from' || k === 'to');
}

const entityNameCache = new Map<string, string | null>();

async function fetchEntityName(kind: EntityKind, id: string): Promise<string | null> {
  try {
    if (kind === 'instructor') {
      const res: any = await apiService.getInstructorById(id);
      const d = res?.data ?? res;
      return d?.instructor?.name ?? d?.name ?? null;
    }
    if (kind === 'class') {
      const res: any = await apiService.getClassById(id);
      const d = res?.data ?? res;
      return d?.class?.className ?? d?.className ?? d?.name ?? null;
    }
    if (kind === 'user') {
      const res: any = await apiService.getUserById(id);
      const d = res?.data ?? res;
      return d?.user?.name ?? d?.name ?? null;
    }
    if (kind === 'studio') {
      const res: any = await apiService.getStudioById(id);
      const d = res?.data ?? res;
      return d?.studio?.studioName ?? d?.studioName ?? d?.name ?? null;
    }
  } catch {
    return null;
  }
  return null;
}

/** Resolves a stored id (or { id, name }) to the label an admin can read. Never shows the raw ObjectId. */
const EntityRefValue: React.FC<{ id?: string; name?: string; kind: EntityKind | null }> = ({
  id,
  name: embeddedName,
  kind,
}) => {
  const cacheKey = kind && id ? `${kind}:${id}` : id ? `id:${id}` : null;
  const cached = cacheKey ? entityNameCache.get(cacheKey) : undefined;
  const [resolved, setResolved] = useState<string | null | undefined>(
    embeddedName || cached !== undefined ? (embeddedName ?? cached) : undefined,
  );

  useEffect(() => {
    if (embeddedName) {
      setResolved(embeddedName);
      return;
    }
    if (!id || !kind) {
      setResolved(null);
      return;
    }
    if (entityNameCache.has(`${kind}:${id}`)) {
      setResolved(entityNameCache.get(`${kind}:${id}`));
      return;
    }
    let cancelled = false;
    fetchEntityName(kind, id).then((n) => {
      entityNameCache.set(`${kind}:${id}`, n);
      if (!cancelled) setResolved(n);
    });
    return () => {
      cancelled = true;
    };
  }, [id, kind, embeddedName]);

  if (resolved === undefined) {
    return <Skeleton width={96} sx={{ display: 'inline-block' }} />;
  }
  return (
    <Typography variant="body2" component="span">
      {resolved || 'Unknown'}
    </Typography>
  );
};

/**
 * Renders any audit value (scalar, date, array, or nested object) in a form a
 * normal admin can read at a glance. Session-range filters store their days as
 * numeric daysOfWeek — special-cased into weekday names since a bare "[1]" chip
 * means nothing without that context. Everything else stays generic so it
 * covers metadata shapes from every module, not just sessions.
 */
const MetaValue: React.FC<{ value: any; parentKey?: string }> = ({ value, parentKey }) => {
  const isObjectArray = Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null;
  const [expanded, setExpanded] = useState(isObjectArray);

  if (value === null || value === undefined) {
    return <Typography variant="body2" color="textSecondary" component="span">—</Typography>;
  }

  if (typeof value === 'string' && ISO_DATETIME_RE.test(value)) {
    return <Typography variant="body2" component="span">{formatISTDateTime(value)} IST</Typography>;
  }

  if (typeof value === 'string' && DATE_ONLY_RE.test(value)) {
    return <Typography variant="body2" component="span">{formatDateOnly(value)}</Typography>;
  }

  if (typeof value === 'string' && TIME_ONLY_RE.test(value)) {
    return <Typography variant="body2" component="span">{formatTimeOnly(value)} IST</Typography>;
  }

  const kind = inferEntityKind(parentKey);
  const named = typeof value === 'object' && !Array.isArray(value) ? extractNamedRef(value) : null;
  if (named && (named.name || (named.id && kind))) {
    return <EntityRefValue id={named.id} name={named.name} kind={kind} />;
  }

  if (typeof value === 'string' && OBJECT_ID_RE.test(value) && kind) {
    return <EntityRefValue id={value} kind={kind} />;
  }

  if (isFromToObject(value)) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
        <MetaValue value={value.from} parentKey={parentKey} />
        <Typography variant="caption" color="text.secondary">→</Typography>
        <MetaValue value={value.to} parentKey={parentKey} />
      </Box>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <Typography variant="body2" color="textSecondary" component="span">None</Typography>;
    }
    if (parentKey === 'daysOfWeek' && value.every((v) => typeof v === 'number')) {
      return (
        <Typography variant="body2" component="span">
          {value.map((d) => DAY_NAMES[d] ?? d).join(', ')}
        </Typography>
      );
    }
    const itemKind = inferEntityKind(parentKey);
    const formatPrimitiveArrayItem = (item: any) => {
      if (typeof item === 'string' && ISO_DATETIME_RE.test(item)) {
        return formatISTDateTime(item);
      }
      if (typeof item === 'string' && DATE_ONLY_RE.test(item)) {
        return formatDateOnly(item);
      }
      if (typeof item === 'string' && TIME_ONLY_RE.test(item)) {
        return `${formatTimeOnly(item)} IST`;
      }
      const ref = extractNamedRef(item);
      if (ref?.name) return ref.name;
      return String(item);
    };

    const resolveChip = (item: any, idx: number) => {
      if (typeof item === 'string' && OBJECT_ID_RE.test(item) && itemKind) {
        return <EntityRefValue key={idx} id={item} kind={itemKind} />;
      }
      const ref = extractNamedRef(item);
      if (ref && (ref.name || (ref.id && itemKind))) {
        return <EntityRefValue key={idx} id={ref.id} name={ref.name} kind={itemKind} />;
      }
      return (
        <Chip
          key={idx}
          size="small"
          variant="outlined"
          label={formatPrimitiveArrayItem(item)}
          sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
        />
      );
    };

    return (
      <Box component="span">
        <Chip
          size="small"
          label={`${value.length} item${value.length === 1 ? '' : 's'} — ${expanded ? 'hide' : 'view'}`}
          onClick={() => setExpanded((e) => !e)}
          sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
        />
        {expanded && (
          <Box sx={{ mt: 0.75 }}>
            {isObjectArray ? (
              <ObjectArrayTable items={value} />
            ) : (
              <Box
                sx={{
                  p: 0.75,
                  bgcolor: '#f8fafc',
                  border: `1px solid ${colors.divider}`,
                  borderRadius: 1,
                  maxHeight: 180,
                  overflow: 'auto',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  alignItems: 'center',
                }}
              >
                {value.map((item, idx) => resolveChip(item, idx))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(([k, v]) => k !== '_id' && k !== 'id' && !isEmptyMetaValue(v));
    if (entries.length === 0) {
      return <Typography variant="body2" color="textSecondary" component="span">—</Typography>;
    }
    return (
      <Box component="span" sx={{ display: 'block', width: '100%' }}>
        <MetaFieldGrid entries={entries} />
      </Box>
    );
  }

  // A raw id whose field name isn't one of the 4 kinds inferEntityKind knows
  // (session, pack, booking, etc.) has no resolver — shown as the id itself,
  // not hidden. A blank cell reads as "nothing happened here", which is worse
  // than an ugly id: it silently drops the one thing the log entry was
  // recording. Whoever hits this in practice is the signal to add that kind.
  if (typeof value === 'string' && OBJECT_ID_RE.test(value)) {
    return (
      <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
        {value}
      </Typography>
    );
  }

  return <Typography variant="body2" component="span" sx={{ fontSize: '0.8125rem' }}>{String(value)}</Typography>;
};

/** Renders an array of similarly-shaped objects (e.g. deletedSample, skippedSessions) as a compact table instead of a JSON dump. */
const ObjectArrayTable: React.FC<{ items: any[] }> = ({ items }) => {
  const keys: string[] = Array.from(
    items.reduce((set: Set<string>, item) => {
      Object.keys(item ?? {}).forEach((k) => set.add(k));
      return set;
    }, new Set<string>()),
  );
  const hasHumanName = keys.some((k) => (NAME_FIELDS as readonly string[]).includes(k));
  const displayKeys = keys.filter((k) => {
    if (k === '_id' || k === 'id') return false;
    if (hasHumanName && k === 'class') return false;
    return true;
  });

  return (
    <TableContainer sx={{ maxHeight: 260, overflow: 'auto', border: `1px solid ${colors.divider}`, borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f9fafb' }}>
            {displayKeys.map((k) => (
              <TableCell key={k} sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                {humanizeKey(k)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, i) => (
            <TableRow key={i}>
              {displayKeys.map((k) => (
                <TableCell key={k} sx={{ fontSize: '0.75rem' }}>
                  <MetaValue value={item?.[k]} parentKey={k} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ExpandableRow: React.FC<{ log: AuditLog; onNavigate: (path: string) => void }> = ({ log, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [bookingDetails, setBookingDetails] = useState<any>(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const isBookingLog = log.targetModel === 'Booking' && log.targetId;
  const hasMetadata = filterUsefulMetadataEntries(log.metadata).length > 0;
  const hasExpandableContent = log.changes.length > 0 || isBookingLog || hasMetadata;

  const handleToggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && isBookingLog && !bookingDetails && !isFetchingDetails) {
      setIsFetchingDetails(true);
      try {
        const res = await apiService.getBookingById(log.targetId);
        setBookingDetails(res?.data ?? res);
      } catch {
        // silently ignore — target booking may have been deleted
      } finally {
        setIsFetchingDetails(false);
      }
    }
  };

  const booking = bookingDetails?.booking ?? bookingDetails;
  const sessionTitle = booking?.session?.className ?? booking?.event?.eventName ?? booking?.wellnessService?.serviceName ?? null;
  const sessionDate = booking?.session?.date ?? booking?.event?.date ?? booking?.wellnessSlotStart ?? null;
  const userName = booking?.user?.name ?? log.metadata?.userId ?? null;
  const userEmail = booking?.user?.email ?? null;
  const userPhone = booking?.user?.phoneNumber ?? null;
  const studioName = booking?.studio?.studioName ?? null;

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell padding="checkbox">
          {hasExpandableContent && (
            <IconButton size="small" onClick={handleToggle}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            {ACTION_LABELS[log.action] ?? log.action}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {MODULE_LABELS[log.module] ?? log.module}
          </Typography>
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body2" fontWeight={600}>
              {log.targetDescription || log.targetId}
            </Typography>
            {isBookingLog && (
              <Tooltip title="Open booking">
                <IconButton size="small" onClick={() => onNavigate(`/bookings/${log.targetId}`)} sx={{ p: 0.25 }}>
                  <OpenInNew sx={{ fontSize: 14, color: colors.textSecondary }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
            {log.targetId}
          </Typography>
        </TableCell>
        <TableCell>
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label={log.performedBy.id === 'system' ? 'system' : log.performedBy.role === 'user' ? 'member' : log.performedBy.role.replace('_', ' ')}
                size="small"
                sx={{
                  backgroundColor: ROLE_COLORS[log.performedBy.role] ?? colors.textSecondary,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  textTransform: 'capitalize',
                }}
              />
              {log.source === 'partner_app' && (
                <Chip label="Partner App" size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
              )}
              {log.source === 'admin_panel' && (
                <Chip label="Admin" size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
              )}
              {log.source === 'studio_web' && (
                <Chip label="Booking Website" size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
              )}
            </Box>
            <Typography variant="body2" fontWeight={600}>{log.performedBy.name}</Typography>
            <Typography variant="caption" color="textSecondary">{log.performedBy.email}</Typography>
            {log.performedBy.studioName && (
              <Typography variant="caption" color="textSecondary">
                {log.performedBy.studioName}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {new Date(log.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
          </Typography>
        </TableCell>
      </TableRow>
      {hasExpandableContent && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ m: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                {/* Booking / Entity Details */}
                {isBookingLog && (
                  <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.5 }}>
                    <Typography variant="caption" fontWeight={700} color="textSecondary" mb={1} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Booking Details
                    </Typography>
                    {isFetchingDetails ? (
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Skeleton width="60%" height={18} />
                        <Skeleton width="45%" height={18} />
                        <Skeleton width="50%" height={18} />
                      </Box>
                    ) : booking ? (
                      <Box display="flex" flexWrap="wrap" gap={2}>
                        {userName && (
                          <Box>
                            <Typography variant="caption" color="textSecondary">User</Typography>
                            <Typography variant="body2" fontWeight={600}>{userName}</Typography>
                            {userEmail && <Typography variant="caption" color="textSecondary" display="block">{userEmail}</Typography>}
                            {userPhone && <Typography variant="caption" color="textSecondary" display="block">{userPhone}</Typography>}
                          </Box>
                        )}
                        {sessionTitle && (
                          <Box>
                            <Typography variant="caption" color="textSecondary">Session / Event</Typography>
                            <Typography variant="body2" fontWeight={600}>{sessionTitle}</Typography>
                            {sessionDate && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                {new Date(sessionDate).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' })}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {studioName && (
                          <Box>
                            <Typography variant="caption" color="textSecondary">Studio</Typography>
                            <Typography variant="body2" fontWeight={600}>{studioName}</Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography variant="caption" color="textSecondary">Booking ID</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                            {booking.bookingId ?? log.targetId}
                          </Typography>
                        </Box>
                        {booking.bookingStatus && (
                          <Box>
                            <Typography variant="caption" color="textSecondary">Current Status</Typography>
                            <Typography variant="body2" fontWeight={600}>{booking.bookingStatus}</Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Booking details unavailable (may have been deleted) — ID: <code>{log.targetId}</code>
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Field Changes */}
                {log.changes.length > 0 && (
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="textSecondary" mb={1} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Changes
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          <TableCell>Old Value</TableCell>
                          <TableCell>New Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {log.changes.map((change, i) => (
                          <TableRow key={i}>
                            <TableCell><code>{humanizeKey(change.field)}</code></TableCell>
                            <TableCell sx={{ color: colors.error }}>
                              <MetaValue value={change.oldValue} parentKey={change.field} />
                            </TableCell>
                            <TableCell sx={{ color: colors.success }}>
                              <MetaValue value={change.newValue} parentKey={change.field} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Metadata */}
                {filterUsefulMetadataEntries(log.metadata).length > 0 && (
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="textSecondary" mb={0.75} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Additional Info
                    </Typography>
                    {(() => {
                      const metadataEntries = filterUsefulMetadataEntries(log.metadata);
                      const { gridEntries, wideEntries } = expandMetadataForGrid(metadataEntries);

                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {gridEntries.length > 0 && <MetaFieldGrid entries={gridEntries} />}
                          {wideEntries.map(([k, v]) => (
                            <Box key={k}>
                              <Box sx={metaSectionLabelSx}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.2 }}>
                                  {humanizeKey(k)}
                                </Typography>
                              </Box>
                              <MetaValue value={v} parentKey={k} />
                            </Box>
                          ))}
                        </Box>
                      );
                    })()}
                  </Box>
                )}

              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const AuditLogsPage: React.FC = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = admin?.role === 'super_admin' || admin?.role === 'admin';

  const [sourceTab, setSourceTab] = useState<AuditSourceTab>(
    isSuperAdmin ? 'all' : 'partner_app',
  );
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;

  const fetchLogs = async (p = 1, tab: AuditSourceTab = sourceTab) => {
    setIsLoading(true);
    try {
      const params: Record<string, any> = { page: p, limit };
      if (searchEmail) params.performerEmail = searchEmail;
      if (moduleFilter) params.module = moduleFilter;
      if (tab === 'admin_panel' || tab === 'partner_app' || tab === 'studio_web') {
        params.source = tab;
      } else if (roleFilter) {
        params.performerRole = roleFilter;
      }

      const res = await apiService.getAuditLogs(params);
      const data = res?.data ?? res;
      setLogs(data.logs ?? []);
      setTotalPages(data.totalPages ?? 1);
      setTotal(data.total ?? 0);
      setPage(p);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(1, sourceTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceTab]);

  const handleSearch = () => fetchLogs(1, sourceTab);

  const handleTabChange = (_: React.SyntheticEvent, value: AuditSourceTab) => {
    setRoleFilter('');
    setSourceTab(value);
  };

  const tabDescription =
    sourceTab === 'partner_app'
      ? 'Actions from the partner app — pack assign/CRUD, booking status updates, and more'
      : sourceTab === 'admin_panel'
        ? 'Actions performed from the admin panel by platform admins'
        : 'All admin and partner actions logged with who made the change and what changed';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color={colors.textPrimary}>
            Audit Logs
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {tabDescription}
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${colors.divider}`,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={sourceTab}
          onChange={handleTabChange}
          variant="fullWidth"
          TabIndicatorProps={{ sx: { height: 3, borderRadius: '3px 3px 0 0', bgcolor: colors.primary } }}
          sx={{
            minHeight: 64,
            bgcolor: colors.background,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              minHeight: 64,
              py: 1.5,
              color: colors.textSecondary,
              gap: 1,
            },
            '& .Mui-selected': {
              color: `${colors.primaryDark} !important`,
              bgcolor: `${colors.primary}12`,
            },
            '& .MuiTab-iconWrapper': { mb: '0 !important', fontSize: 22 },
          }}
        >
          {isSuperAdmin && (
            <Tab
              icon={<Apps />}
              iconPosition="start"
              label="All"
              value="all"
            />
          )}
          <Tab
            icon={<AdminPanelSettings />}
            iconPosition="start"
            label="Admin Panel"
            value="admin_panel"
          />
          <Tab
            icon={<PhoneIphone />}
            iconPosition="start"
            label="Partner App"
            value="partner_app"
          />
          <Tab
            icon={<Language />}
            iconPosition="start"
            label="Booking Website"
            value="studio_web"
          />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              size="small"
              placeholder="Search by email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 240 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Module</InputLabel>
              <Select
                value={moduleFilter}
                label="Module"
                onChange={(e) => setModuleFilter(e.target.value)}
              >
                <MenuItem value="">All Modules</MenuItem>
                <MenuItem value="packs">Packs</MenuItem>
                <MenuItem value="bookings">Bookings</MenuItem>
                <MenuItem value="wellness">Wellness</MenuItem>
                <MenuItem value="studios">Studios</MenuItem>
                <MenuItem value="sessions">Sessions</MenuItem>
                <MenuItem value="events">Events</MenuItem>
                <MenuItem value="classes">Classes</MenuItem>
                <MenuItem value="users">Users</MenuItem>
                <MenuItem value="studio-users">Studio Users</MenuItem>
                <MenuItem value="instructors">Instructors</MenuItem>
                <MenuItem value="waitlist">Waitlist</MenuItem>
                <MenuItem value="user-notes">Member Notes</MenuItem>
              </Select>
            </FormControl>
            {isSuperAdmin && sourceTab === 'all' && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Role</InputLabel>
                <Select value={roleFilter} label="Role" onChange={(e) => setRoleFilter(e.target.value)}>
                  <MenuItem value="">All Roles</MenuItem>
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                  <MenuItem value="brand_owner">Brand Owner</MenuItem>
                  <MenuItem value="studio_owner">Studio Owner</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="instructor">Instructor</MenuItem>
                </Select>
              </FormControl>
            )}
            <Tooltip title="Apply filters">
              <span>
                <IconButton onClick={handleSearch} size="small" sx={{ bgcolor: colors.primary, color: '#fff', '&:hover': { bgcolor: colors.primary } }}>
                  <Search fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="body2" color="textSecondary" ml="auto">
              {total} log{total !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : logs.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}>
              <Typography color="textSecondary">No audit logs found</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                    <TableCell width={40} />
                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Performed By</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Timestamp (IST)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <ExpandableRow key={log._id} log={log} onNavigate={navigate} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" p={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => fetchLogs(p, sourceTab)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuditLogsPage;
